// Tab nav for the BMD report dashboard. Sits directly under
// `BmdDashboardHeader` on the same dark surface.

import React from "react"

export type BmdDashboardTabKey =
  | "main"
  | "mastery"
  | "chain"
  | "skills"
  | "sb"

// Mastery Breakdown sits straight after Main Report: it is the drill-down of
// the same headline picture, so it reads as the next question a tutor asks.
//
// The last tab is "Remediation", not "Skill Builders". It was renamed to
// "Skill Builders" once and the PM asked again for "Remediation" - the tab is
// the whole remediation surface, while "Skill Builders" is the thing listed
// inside it, which is why the COLUMN in that table is headed Skill Builders.
const TABS: ReadonlyArray<{ key: BmdDashboardTabKey; label: string }> = [
  { key: "main", label: "Main Report" },
  { key: "mastery", label: "Mastery Breakdown" },
  { key: "chain", label: "Chain Analysis" },
  { key: "skills", label: "Skills Gap" },
  { key: "sb", label: "Remediation" },
] as const

const SURFACE = "#1f2a44"
const ACCENT = "#ff5a5a"

export type BmdDashboardTabsProps = {
  active: BmdDashboardTabKey
  onChange: (key: BmdDashboardTabKey) => void
  /**
   * Tabs to leave out. English hides "sb": there are no English Skill Builders to remediate
   * with yet, and an empty Remediation tab reads as a broken one rather than an unbuilt one.
   * Omitting this shows every tab, so maths is untouched. When English skill builders land,
   * stop passing the key and the tab is back - no other change.
   */
  hiddenKeys?: BmdDashboardTabKey[]
}

export const BmdDashboardTabs: React.FC<BmdDashboardTabsProps> = ({
  active,
  onChange,
  hiddenKeys = [],
}) => {
  const tabs = TABS.filter((t) => !hiddenKeys.includes(t.key))
  return (
    <div
      className="px-3 d-flex flex-wrap rounded-bottom mb-4"
      style={{
        backgroundColor: SURFACE,
        borderTop: "1px solid rgba(255,255,255,0.08)",
      }}
    >
      {tabs.map((t) => {
        const isActive = t.key === active
        return (
          <button
            key={t.key}
            type="button"
            onClick={() => onChange(t.key)}
            className="btn btn-link text-decoration-none px-3 py-2 font-size-14"
            style={{
              color: isActive ? "white" : "rgba(255,255,255,0.6)",
              fontWeight: isActive ? 600 : 400,
              borderBottom: isActive
                ? `3px solid ${ACCENT}`
                : "3px solid transparent",
              borderRadius: 0,
              boxShadow: "none",
            }}
          >
            {t.label}
          </button>
        )
      })}
    </div>
  )
}

export default BmdDashboardTabs
